import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFormattedDate, convertToNumberKWD } from "../../utils/common";
import {
  calculateVariantItemPrice,
  calculateChargeableItems,
  calculateItemDisplayPrice,
  calculateTotalChargeableItemsCost,
  calculateTotalPackageBasePrice,
  calculatePackagePrice,
  calculateItemDisplayPriceInPackage,
  getSelectedOptions,
  transformModifyOrderPayload,
  transformCombinedModifyOrderPayload,
  transformCombinedUnpaidOrderPayload,
  transformCombinedReorderPayload,
  getCateringItemPrice,
  itemRequiresAddOnSelection,
  shouldShowAddOnOptions
} from "../../utils/catering";
import { getCategoryLimitsForPax } from "../../utils/paxCalculations";
import { validatePackageCompletion } from "../../utils/paxCalculations";
import ShareQuotationIcon from "../../assets/icons/ShareQuotationIcon";
import ShareQuotationModal from "./ShareQuotationModal";
import { useSelector, useDispatch } from "react-redux";

import { selectCateringCartPackages, selectCateringCartItems, selectCateringCartSubtotal, selectCateringCartTotal, selectCateringCartFilters, selectCateringRestaurantId, selectCateringBrandsList, selectCateringCheckoutCart, selectIsModifyOrderMode, selectModifyOrderPax, selectModifyOrderId, selectModifyOrderOriginalTotal, selectIsUnpaidOrderMode, selectUnpaidOrderId, selectUnpaidCombineOrderId, selectCateringCartNotes, selectCateringAreaCharges, selectCateringAdditionalServiceFilters, selectCateringDeliveryCharge, selectCateringDiscountPrice } from "../../redux/features/restaurants/restaurantSelectors";
import { setCateringCartItems, addCateringCartVariantItem, removeCateringCartVariantItem, setCateringCartSubtotal, setCateringCartTotal, removeCateringCartPackage, setCateringCheckoutCart, addItemToCateringPackage, removeItemFromCateringPackage, addVariantItemToCateringPackage, removeVariantItemFromCateringPackage, removeCateringPackageCompletely, setCateringCartIds, clearModifyOrderMode, setCateringCartNotes, setCateringAreaCharges, setCateringDeliveryCharge, setCateringDiscountPrice } from "../../redux/features/restaurants/restaurantSlice";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import { selectCateringSearchedCustomer, selectCateringSelectedAddress } from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import { selectSearchedCompany, selectCompanySelectedAddress } from "../../redux/features/company/companySelectors";
import { getCateringAreaCharges } from "../../api/get/getCateringAreaCharges";

import ShareBankDetailsModal from "./ShareBankDetailsModal";
import { updateCateringPaidOrder } from "../../api/post/updateCateringPaidOrder";
import { updateCateringUnpaidOrder } from "../../api/post/updateCateringUnpaidOrder";
import { updateCombinedCateringPaidOrder } from "../../api/post/updateCombinedCateringPaidOrder";
import { updateCombinedCateringUnpaidOrder } from "../../api/post/updateCombinedCateringUnpaidOrder";
import AddOnSelectionModal from "../menu/AddOnSelectionModal";
import RemoveItemWarningModal from "../menu/RemoveItemWarningModal";

const CateringOrderSummary = ({ selectedPackages, showPackageDetailButtons = false, showPaymentOptions = false, disabled = false, isModifyOrderMode = false }) => {
  // Get modify order mode flags from Redux
  const reduxIsModifyOrderMode = useSelector(selectIsModifyOrderMode);
  const reduxModifyOrderPax = useSelector(selectModifyOrderPax);
  const reduxModifyOrderId = useSelector(selectModifyOrderId);
  const reduxModifyOrderOriginalTotal = useSelector(selectModifyOrderOriginalTotal);
  
  // Get unpaid order mode flags from Redux
  const reduxIsUnpaidOrderMode = useSelector(selectIsUnpaidOrderMode);
  const reduxUnpaidOrderId = useSelector(selectUnpaidOrderId);
  const reduxUnpaidCombineOrderId = useSelector(selectUnpaidCombineOrderId);
  
  // Only apply modify order mode if explicitly passed OR if Redux state says so
  // For combined orders, modifyOrderPax may be null since each package has its own PAX
  // In that case, check if modifyOrderId is set to determine if we're in modify mode
  const actualModifyMode = (isModifyOrderMode || reduxIsModifyOrderMode) && 
                          (reduxModifyOrderPax !== null || reduxModifyOrderId !== null);
  
  // Check if in unpaid order mode
  const actualUnpaidMode = reduxIsUnpaidOrderMode && reduxUnpaidOrderId !== null;
  
  console.log('CateringOrderSummary actualModifyMode (from Redux):', {
    actualModifyMode,
    isModifyOrderModeProp: isModifyOrderMode,
    reduxIsModifyOrderMode,
    reduxModifyOrderPax,
    reduxModifyOrderId,
    reduxModifyOrderOriginalTotal,
    actualUnpaidMode,
    reduxIsUnpaidOrderMode,
    reduxUnpaidOrderId,
    reduxUnpaidCombineOrderId,
    reason: actualModifyMode ? (reduxModifyOrderPax !== null ? 'Has PAX (single order)' : 'Has Order ID (combined order)') : 'Not in modify mode'
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formattedDate = getFormattedDate();
  const filters = useSelector(selectCateringCartFilters);

  const [orderedPackages, setOrderedPackages] = useState([]);
  const [orderedItems, setOrderedItems] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [packageToRemove, setPackageToRemove] = useState(null);
  const [showProceedToPayment, setShowProceedToPayment] = useState(false);
  const [showPendingPayment, setShowPendingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showShareQuotationAfterAdd, setShowShareQuotationAfterAdd] = useState(false);
  const [hasPackageBeenModified, setHasPackageBeenModified] = useState(false);
  const [isModifyingOrder, setIsModifyingOrder] = useState(false);
  const [modifyOrderError, setModifyOrderError] = useState(null);
  const [checkoutNotes, setCheckoutNotes] = useState(""); // Track notes from when package was added to cart
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [itemForAddOnSelection, setItemForAddOnSelection] = useState(null);
  const [showRemoveWarningModal, setShowRemoveWarningModal] = useState(false);
  const [itemForRemoveWarning, setItemForRemoveWarning] = useState(null);
  const [addOnItemsForWarning, setAddOnItemsForWarning] = useState([]);
  const [packageInfoForRemoveWarning, setPackageInfoForRemoveWarning] = useState(null);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [isEditingDeliveryCharge, setIsEditingDeliveryCharge] = useState(false);
  const [deliveryChargeInput, setDeliveryChargeInput] = useState("");
  const [discountPrice, setDiscountPrice] = useState(0);
  const [isEditingDiscount, setIsEditingDiscount] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  
  // Get area charges from Redux
  const areaChargesFromRedux = useSelector(selectCateringAreaCharges);
  const deliveryChargeFromRedux = useSelector(selectCateringDeliveryCharge);
  const discountPriceFromRedux = useSelector(selectCateringDiscountPrice);
  const brandList = useSelector(selectCateringBrandsList);
  const cateringRestaurantId = useSelector(selectCateringRestaurantId);
  const cateringRestaurantName = brandList?.find(brand => brand.id == cateringRestaurantId)?.name_en;
  const serviceType = useSelector(selectServiceType);
  
  // Get notes from Redux
  const notes = useSelector(selectCateringCartNotes);
  
  // Individual catering selectors
  const cateringCustomerInfo = useSelector(selectCateringSearchedCustomer);
  const cateringSelectedAddress = useSelector(selectCateringSelectedAddress);
  
  // Corporate catering selectors
  const companyInfo = useSelector(selectSearchedCompany);
  const companySelectedAddress = useSelector(selectCompanySelectedAddress);



  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const cateringCartItems = useSelector(selectCateringCartItems);
  const cateringCartSubtotal = useSelector(selectCateringCartSubtotal);
  const cateringCartTotal = useSelector(selectCateringCartTotal);
  const checkoutCart = useSelector(selectCateringCheckoutCart);
  const additionalServiceFilters = useSelector(selectCateringAdditionalServiceFilters);

  // Validate if all required package items have been added based on selection_rules
  // Note: Now supports multiple packages (main package + additional services)
  const packageValidation = useMemo(() => {
    if (!orderedPackages || orderedPackages.length === 0) {
      return {
        isValid: false,
        missingCategories: [],
        message: 'No package in cart'
      };
    }

    // Validate all packages - each package must be complete
    const validationResults = orderedPackages.map(pkg => {
      const paxToUse = pkg.isAdditionalService 
        ? (pkg.pax || 20)
        : filters.pax;
      
      console.log('📋 Validating package:', {
        packageId: pkg.id,
        packageName: pkg.name_en,
        isAdditionalService: pkg.isAdditionalService,
        paxToUse,
        itemsCount: pkg.items?.length || 0
      });
      
      const validation = validatePackageCompletion(pkg, paxToUse);
      
      console.log('📋 Validation result:', {
        packageName: pkg.name_en,
        isValid: validation.isValid,
        missingCategories: validation.missingCategories
      });
      
      return {
        package: pkg,
        validation
      };
    });

    // Check if any package is incomplete
    const incompletePackages = validationResults.filter(result => !result.validation.isValid);
    
    if (incompletePackages.length > 0) {
      const totalMissingCategories = incompletePackages.reduce((total, result) => 
        total + (result.validation.missingCategories?.length || 0), 0
      );
      console.log('❌ Validation failed:', {
        incompletePackages: incompletePackages.map(r => ({
          name: r.package.name_en,
          missing: r.validation.missingCategories
        })),
        totalMissing: totalMissingCategories
      });
      return {
        isValid: false,
        missingCategories: incompletePackages.flatMap(result => result.validation.missingCategories || []),
        message: `Missing items in ${totalMissingCategories} categories across ${incompletePackages.length} package(s)`
      };
    }

    console.log('✅ All packages validated successfully');
    return {
      isValid: true,
      missingCategories: [],
      message: 'All packages are complete'
    };
  }, [orderedPackages, filters.pax]);
  // Check if there are items in the catering cart AND all required items are added
  const hasItemsInCart = cateringCartPackages.length > 0 || (orderedItems && orderedItems.length > 0);
  const canAddToCart = hasItemsInCart && packageValidation.isValid;

  // Check if current packages are already in checkout cart
  const isPackageInCheckoutCart = useMemo(() => {
    if (!orderedPackages || orderedPackages.length === 0 || !checkoutCart || checkoutCart.length === 0) {
      return false;
    }
    // Check if all ordered packages are in checkout cart
    return orderedPackages.every(pkg => 
      checkoutCart.some(checkoutPkg => checkoutPkg.id === pkg.id)
    );
  }, [orderedPackages, checkoutCart]);

  // Check if any package has been modified compared to what's in checkout cart
  const isPackageModified = useMemo(() => {
    if (!isPackageInCheckoutCart || !orderedPackages || !checkoutCart) {
      return false;
    }

    // Check if notes have been modified
    if (notes !== checkoutNotes) return true;

    // Check if any package has been modified
    return orderedPackages.some(currentPkg => {
      const checkoutPkg = checkoutCart.find(cp => cp.id === currentPkg.id);
      if (!checkoutPkg) return true; // Package not found in checkout

      // Compare items arrays
      const currentItems = currentPkg.items || [];
      const checkoutItems = checkoutPkg.items || [];

      // If different number of items, it's modified
      if (currentItems.length !== checkoutItems.length) return true;

      // Compare each item
      return currentItems.some(currentItem => {
        // Find matching item by ID AND add_on_type (to distinguish half/full/normal)
        const checkoutItem = checkoutItems.find(ci => 
          ci.id === currentItem.id && 
          (ci.add_on_type || null) === (currentItem.add_on_type || null)
        );
        if (!checkoutItem) return true; // Item not found in checkout, it's modified

        // Compare quantities
        if (currentItem.quantity !== checkoutItem.quantity) return true;

        // Compare complimentary counts (important for detecting complementary changes)
        const currentComplimentary = currentItem.complimentaryCount || 0;
        const checkoutComplimentary = checkoutItem.complimentaryCount || 0;
        if (currentComplimentary !== checkoutComplimentary) return true;

        // For variant items, compare variant details
        if (currentItem.has_variants && currentItem.items) {
          const currentVariants = currentItem.items || [];
          const checkoutVariants = checkoutItem.items || [];
          
          if (currentVariants.length !== checkoutVariants.length) return true;

          return currentVariants.some(currentVariant => {
            const checkoutVariant = checkoutVariants.find(cv => cv.variantId === currentVariant.variantId);
            if (!checkoutVariant) return true;
            return currentVariant.quantity !== checkoutVariant.quantity;
          });
        }

        return false;
      });
    });
  }, [orderedPackages, checkoutCart, isPackageInCheckoutCart, notes, checkoutNotes]);


  useEffect(() => {
    // Always use cateringCartPackages instead of selectedPackages prop
    // Filter packages by current restaurant to prevent showing packages from different restaurants
    // Multiple packages are now allowed (main package + additional services)
    if (cateringCartPackages && cateringCartPackages.length > 0) {
      const filteredPackages = cateringCartPackages.filter(pkg => {
        // If package has restaurant_id, filter by it
        if (pkg.restaurant_id && cateringRestaurantId) {
          return pkg.restaurant_id === cateringRestaurantId;
        }
        // If no restaurant_id in package, include it (backward compatibility)
        return true;
      });
      
      // Allow multiple packages (main package + additional services)
      setOrderedPackages(filteredPackages.map(pkg => ({
        ...pkg,
        items: Array.isArray(pkg.items) ? pkg.items.map(item => ({ ...item })) : []
      })));
    } else {
      setOrderedPackages([]);
    }
  }, [cateringCartPackages, cateringRestaurantId]);
  // Calculate total chargeable items cost - calculate each package with its own PAX
  const totalChargeableItemsCost = useMemo(() => {
    return orderedPackages.reduce((total, pkg) => {
      const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
      return total + calculateTotalChargeableItemsCost([pkg], paxToUse);
    }, 0);
  }, [orderedPackages, filters.pax]);

  // Calculate total package base price - calculate each package with its own PAX
  const totalPackageBasePrice = useMemo(() => {
    return orderedPackages.reduce((total, pkg) => {
      const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
      return total + calculateTotalPackageBasePrice([pkg], paxToUse);
    }, 0);
  }, [orderedPackages, filters.pax]);



  // Calculate subtotal (before delivery and discount)
  const subtotal = useMemo(() => {
    return totalPackageBasePrice + totalChargeableItemsCost;
  }, [totalPackageBasePrice, totalChargeableItemsCost]);

  // Calculate final total payable amount (including delivery charge, excluding discount)
  const totalPayable = useMemo(() => {
    return Math.max(0, subtotal + deliveryCharge - discountPrice);
  }, [subtotal, deliveryCharge, discountPrice]);

  // Derive orderedItems from orderedPackages to keep nested model in sync
  useEffect(() => {
    const flattened = (orderedPackages || []).flatMap(pkg => (pkg.items || []).map(it => ({ ...it, packageId: pkg.id })));
    setOrderedItems(flattened);
  }, [orderedPackages]);

  // Fetch area charges on component mount (only if not in Redux)
  useEffect(() => {
    const fetchAreaCharges = async () => {
      // Only fetch if not already in Redux (check for undefined or null, not just empty)
      if (areaChargesFromRedux !== undefined && areaChargesFromRedux !== null) {
        console.log("Area charges already in Redux, skipping API call");
        return;
      }

      try {
        console.log("Fetching area charges from API...");
        const response = await getCateringAreaCharges("3");
        console.log("API Response:", response);
        if (response && response.data) {
          dispatch(setCateringAreaCharges(response.data));
          console.log("Area charges loaded and saved to Redux:", response.data);
        } else {
          console.error("No data in API response:", response);
        }
      } catch (error) {
        console.error("Error fetching area charges:", error);
      }
    };

    fetchAreaCharges();
  }, [areaChargesFromRedux, dispatch]);

  // Initialize delivery charge from Redux on mount (to restore edited value)
  useEffect(() => {
    if (deliveryChargeFromRedux > 0) {
      console.log("💰 Initializing delivery charge from Redux:", deliveryChargeFromRedux);
      setDeliveryCharge(deliveryChargeFromRedux);
    }
  }, []);

  // Initialize discount from Redux on mount (to restore edited value)
  useEffect(() => {
    if (discountPriceFromRedux > 0) {
      console.log("💰 Initializing discount from Redux:", discountPriceFromRedux);
      setDiscountPrice(discountPriceFromRedux);
    }
  }, []);

  // Calculate delivery charge based on selected address area ID
  useEffect(() => {
    // Skip if delivery charge was already set from Redux (user edited it)
    if (deliveryChargeFromRedux > 0) {
      console.log("💰 Skipping auto-calculation, using Redux value:", deliveryChargeFromRedux);
      return;
    }

    console.log("🔍 DELIVERY CHARGE CALCULATION DEBUG:");
    console.log("🔍 Service Type:", serviceType);
    console.log("🔍 Full cateringSelectedAddress:", cateringSelectedAddress);
    console.log("🔍 Full companySelectedAddress:", companySelectedAddress);
    console.log("🔍 Area charges from Redux:", areaChargesFromRedux);
    
    let selectedAreaId = null;
    let selectedAreaName = null;
    
    if (serviceType === "individual catering" && cateringSelectedAddress) {
      // For individual catering, get area ID and name from selected address
      selectedAreaId = cateringSelectedAddress.area_id;
      selectedAreaName = cateringSelectedAddress.area;
      console.log("🔍 Individual catering - extracted area_id:", selectedAreaId);
    } else if (serviceType === "corporate catering" && companySelectedAddress) {
      // For corporate catering, get area ID and name from company address
      selectedAreaId = companySelectedAddress.area_id;
      selectedAreaName = companySelectedAddress.area;
      console.log("🔍 Corporate catering - extracted area_id:", selectedAreaId);
    } else {
      console.log("🔍 No address selected or service type not matched");
    }

    console.log("💰 Selected area ID:", selectedAreaId);
    console.log("💰 Selected area name:", selectedAreaName);

    if (areaChargesFromRedux && areaChargesFromRedux.length > 0 && selectedAreaId) {
      console.log("💰 Attempting to match area ID:", selectedAreaId);
      console.log("💰 Available area charges:", areaChargesFromRedux.map(a => ({ id: a.id, name: a.name_en, charge: a.delivery_charge })));
      
      // Match by area ID
      const matchingArea = areaChargesFromRedux.find(
        area => area.id === selectedAreaId || area.id === parseInt(selectedAreaId)
      );
      
      console.log("💰 Matched area:", matchingArea);

      if (matchingArea && matchingArea.delivery_charge) {
        const charge = parseFloat(matchingArea.delivery_charge.replace(/[^\d.]/g, '')) || 0;
        setDeliveryCharge(charge);
        dispatch(setCateringDeliveryCharge(charge));
        console.log("✅ 💰 Delivery charge set to:", charge, "KWD");
      } else {
        setDeliveryCharge(0);
        dispatch(setCateringDeliveryCharge(0));
        console.log("❌ 💰 No matching area found or no delivery charge");
      }
    } else {
      setDeliveryCharge(0);
      dispatch(setCateringDeliveryCharge(0));
      console.log("❌ Missing data:", {
        hasAreaCharges: !!areaChargesFromRedux,
        areaChargesLength: areaChargesFromRedux?.length || 0,
        hasSelectedAreaId: !!selectedAreaId
      });
    }
  }, [serviceType, cateringSelectedAddress, companySelectedAddress, areaChargesFromRedux, deliveryChargeFromRedux, dispatch]);

  // Initialize Redux totals when totals change
  useEffect(() => {
    // Debug logging to see pricing calculations and validation
    console.log('Package Validation Debug:', {
      orderedPackages: orderedPackages.length,
      currentPax: filters.pax,
      totalPackageBasePrice,
      totalChargeableItemsCost,
      totalPayable,
      packageValidation: packageValidation,
      canAddToCart: hasItemsInCart && packageValidation.isValid
    });

    dispatch(setCateringCartTotal(totalPayable));
    dispatch(setCateringCartSubtotal(subtotal));
  }, [totalPayable, subtotal, dispatch, orderedPackages.length, filters.pax, packageValidation, hasItemsInCart]);

  // Check if package is already in checkout cart on mount and show share quotation button
  useEffect(() => {
    if (isPackageInCheckoutCart) {
      setShowShareQuotationAfterAdd(true);
      setShowProceedToPayment(true);
      // Save current notes as checkout notes when package is already in cart
      setCheckoutNotes(notes);
    }
  }, [isPackageInCheckoutCart, notes]);

  // Track when package has been modified
  useEffect(() => {
    if (isPackageInCheckoutCart && isPackageModified) {
      setHasPackageBeenModified(true);
    } else if (!isPackageModified) {
      setHasPackageBeenModified(false);
    }
  }, [isPackageInCheckoutCart, isPackageModified]);

  // Auto-add package to checkout cart when in unpaid mode and package is valid
  useEffect(() => {
    console.log('🔍 Auto-add useEffect check:', {
      actualUnpaidMode,
      orderedPackagesLength: orderedPackages.length,
      isPackageInCheckoutCart,
      packageValidationIsValid: packageValidation.isValid,
      shouldAutoAdd: actualUnpaidMode && orderedPackages.length > 0 && !isPackageInCheckoutCart
    });
    
    if (actualUnpaidMode && orderedPackages.length > 0 && !isPackageInCheckoutCart) {
      console.log('✅ Unpaid mode: Auto-adding package to checkout cart');
      // Filter packages by current restaurant
      const restaurantFilteredPackages = orderedPackages.filter(pkg => {
        if (pkg.restaurant_id && cateringRestaurantId) {
          return pkg.restaurant_id === cateringRestaurantId;
        }
        return true;
      });
      
      console.log('📦 Filtered packages to add:', restaurantFilteredPackages);
      
      // Add to checkout cart even if not fully validated yet
      // Validation will be checked when proceeding to payment
      dispatch(setCateringCheckoutCart(restaurantFilteredPackages));
      setCheckoutNotes(notes);
      setShowProceedToPayment(true);
      setShowShareQuotationAfterAdd(true);
      
      console.log('✅ Package added to checkout cart, flags set', {
        showProceedToPayment: true,
        showShareQuotationAfterAdd: true
      });
    }
  }, [actualUnpaidMode, orderedPackages, isPackageInCheckoutCart, cateringRestaurantId, notes, dispatch, packageValidation]);

  // Remove the problematic useEffect that was causing infinite loop
  // Redux totals will be updated only when there are actual user actions



  // Add item to package (updates local state and cart)
  const handleAddItem = (packageId, itemId) => {
    // In modify order mode, prevent adding new items
    if (actualModifyMode) {
      alert('Cannot add additional items in modify order mode. You can only swap existing items.');
      return;
    }
    
    // Update local ordered packages state
    setOrderedPackages(prevPackages =>
      prevPackages?.map(pkg =>
        pkg.id === packageId
          ? {
            ...pkg,
            items: pkg.items?.map(item =>
              item.id === itemId ? { ...item, quantity: item.quantity + 1 } : item
            ),
          }
          : pkg
      )
    );

    // Also add to catering cart if it's a catering item
    const packageToUpdate = orderedPackages.find(pkg => pkg.id === packageId);
    if (packageToUpdate) {
      const itemToUpdate = packageToUpdate.items?.find(item => item.id === itemId);
      if (itemToUpdate) {
        // Update nested package items in Redux
        dispatch(addItemToCateringPackage({
          packageId,
          item: { ...itemToUpdate, quantity: itemToUpdate.quantity + 1 },
          packageMeta: {
            title: packageToUpdate.name_en || packageToUpdate.title,
            basePrice: calculatePackagePrice(packageToUpdate, filters.pax)
          }
        }));

        // Totals will be recalculated automatically via useEffect
      }
    }
  };

  // Remove item from package (updates local state and cart)
  const handleRemoveItem = (packageId, itemId) => {
    // Update local ordered packages state
    setOrderedPackages(prevPackages =>
      prevPackages?.map(pkg =>
        pkg.id === packageId
          ? {
            ...pkg,
            items: pkg.items?.map(item =>
              item.id === itemId && item.quantity > 0 ? { ...item, quantity: item.quantity - 1 } : item
            ),
          }
          : pkg
      )
    );

    // Also update catering cart
    const packageToUpdate = orderedPackages.find(pkg => pkg.id === packageId);
    if (packageToUpdate) {
      const itemToUpdate = packageToUpdate.items?.find(item => item.id === itemId);
      if (itemToUpdate && itemToUpdate.quantity > 1) {
        // Update nested package items in Redux
        dispatch(addItemToCateringPackage({
          packageId,
          item: { ...itemToUpdate, quantity: itemToUpdate.quantity - 1 },
          packageMeta: {
            title: packageToUpdate.name_en || packageToUpdate.title,
            basePrice: calculatePackagePrice(packageToUpdate, filters.pax)
          }
        }));

        // Totals will be recalculated automatically via useEffect
      } else if (itemToUpdate && itemToUpdate.quantity <= 1) {
        // Remove item from nested package if quantity is 0 or less
        dispatch(removeItemFromCateringPackage({ 
          packageId, 
          itemId,
          addOnType: itemToUpdate.add_on_type || null
        }));

        // Totals will be recalculated automatically via useEffect
      }
    }
  };

  // In modify mode, category limits are flexible to allow swapping items
  // Validation focuses on maintaining minimum total, not category constraints

  // Handle adding more of an item (exact copy from OrderSummary)
  const handleAddCartItem = (item) => {
    // For non-variant items only
    if (!item.has_variants) {
      const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === item.id && (it.add_on_type || null) === (item.add_on_type || null)));
      if (!pkg) return;
      
      // Skip add-on modal for standalone items (price_type: "Standalone Item Based")
      const isStandaloneItem = item.price_type === "Standalone Item Based";
      
      // Check if this item requires add-on selection and if we should show the modal
      // Skip for standalone items - they don't have half/full options
      if (!isStandaloneItem && !item.add_on_type && itemRequiresAddOnSelection(item)) {
        const pkgItems = pkg.items || [];
        const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
        const shouldShowAddOn = shouldShowAddOnOptions(item, pkgItems, pkg, paxToUse);
        
        if (shouldShowAddOn) {
          // Show add-on selection modal
          setItemForAddOnSelection(item);
          setShowAddOnModal(true);
          return;
        }
      }
      
      // Standard add
      dispatch(addItemToCateringPackage({ packageId: pkg.id, item: { ...item, quantity: item.quantity + 1 } }));

      // Totals will be recalculated automatically via useEffect
    }
  };

  // Check if current total matches the original total exactly in modify mode
  const isTotalMismatch = useMemo(() => {
    if (!actualModifyMode || !reduxModifyOrderOriginalTotal) return false;
    
    // Use a small epsilon for floating point comparison (0.001 KWD precision)
    const epsilon = 0.001;
    const difference = Math.abs(totalPayable - reduxModifyOrderOriginalTotal);
    const isMismatch = difference > epsilon;
    
    if (isMismatch) {
      console.log('⚠️ Total does not match original:', {
        currentTotal: totalPayable.toFixed(3),
        originalTotal: reduxModifyOrderOriginalTotal.toFixed(3),
        difference: (totalPayable - reduxModifyOrderOriginalTotal).toFixed(3),
        isAbove: totalPayable > reduxModifyOrderOriginalTotal,
        isBelow: totalPayable < reduxModifyOrderOriginalTotal
      });
    }
    
    return isMismatch;
  }, [actualModifyMode, reduxModifyOrderOriginalTotal, totalPayable]);

  // Handle removing an item (exact copy from OrderSummary)
  const handleRemoveCartItem = (item) => {
    console.log('🗑️ Attempting to remove item:', {
      itemId: item.id,
      itemName: item.name_en || item.name,
      categoryId: item.category_id,
      has_variants: item.has_variants,
      add_on_type: item.add_on_type,
      quantity: item.quantity
    });
    
    // For non-variant items only
    if (!item.has_variants && item.quantity > 0) {
      const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === item.id));
      if (!pkg) return;
      
      console.log('🗑️ Found package:', pkg.id, 'Total items in package:', pkg.items?.length);
      
      // WARNING CHECK: If trying to remove a normal item (no add_on_type), check if there are add-on items in THE SAME CATEGORY
      // Category limit determines if items are chargeable, not individual item limits
      if (!item.add_on_type && item.category_id) {
        const pkgItems = pkg.items || [];
        
        // Find ALL add-on items in the same category (any item with half/full in this category)
        const addOnItemsInCategory = pkgItems.filter(cartItem => 
          cartItem.category_id === item.category_id && 
          cartItem.add_on_type && 
          (cartItem.add_on_type === 'half' || cartItem.add_on_type === 'full')
        );
        
        console.log('🗑️ Checking for add-on items in category:', {
          categoryId: item.category_id,
          itemHasAddOnType: !!item.add_on_type,
          addOnItemsInCategoryFound: addOnItemsInCategory.length,
          addOnItems: addOnItemsInCategory.map(ai => ({ 
            id: ai.id, 
            name: ai.name_en || ai.name, 
            category_id: ai.category_id,
            add_on_type: ai.add_on_type,
            quantity: ai.quantity,
            price: ai.price
          }))
        });
        
        // If there are ANY add-on items in this category, show warning modal
        if (addOnItemsInCategory.length > 0) {
          console.log('⚠️ Cannot remove normal item - add-on items exist in category. Showing warning modal.');
          const packageBrandName = pkg.restaurant_id
            ? (brandList?.find(brand => brand.id == pkg.restaurant_id)?.name_en || cateringRestaurantName)
            : (pkg.isAdditionalService
                ? (brandList?.find(brand => brand.id == pkg.parent_restaurant_id)?.name_en || cateringRestaurantName)
                : cateringRestaurantName);
          setPackageInfoForRemoveWarning({
            packageName: pkg?.name_en || pkg?.title || 'Package',
            brandName: packageBrandName || 'Catering'
          });
          setItemForRemoveWarning(item);
          setAddOnItemsForWarning(addOnItemsInCategory);
          setShowRemoveWarningModal(true);
          return; // Prevent removal
        }
      }
      
      console.log('✅ No add-on items found in category or item itself is add-on. Proceeding with removal.');
      
      // Standard remove
      dispatch(addItemToCateringPackage({ packageId: pkg.id, item: { ...item, quantity: item.quantity - 1 } }));

      // Totals will be recalculated automatically via useEffect
    }
  };

  // Handle adding more of a variant item (exact copy from OrderSummary)
  const handleAddVariantCartItem = (item, variantItem) => {
    // In modify order mode, allow adding - only enforce minimum total at checkout
    const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === item.id));
    if (!pkg) return;
    dispatch(addVariantItemToCateringPackage({ packageId: pkg.id, productId: item.id, variantId: variantItem.variantId }));

    // Totals will be recalculated automatically via useEffect
  };

  // Handle removing a variant item (exact copy from OrderSummary)
  const handleRemoveVariantCartItem = (item, variantItem) => {
    // In modify order mode, allow removal but total validation happens at checkout
    const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === item.id));
    if (!pkg) return;
    dispatch(removeVariantItemFromCateringPackage({ packageId: pkg.id, productId: item.id, variantId: variantItem.variantId }));

    // Totals will be recalculated automatically via useEffect
  };

  // Handle removing a package from catering cart
  const handleRemovePackage = (packageId) => {
    // Find the package to get its details for confirmation
    const packageToRemove = orderedPackages.find(pkg => pkg.id === packageId);
    if (packageToRemove) {
      setPackageToRemove(packageToRemove);
    }
  };

  // Confirm package removal
  const confirmRemovePackage = () => {
    if (packageToRemove) {
      const packagePrice = calculatePackagePrice(packageToRemove, filters.pax);

      // Remove the package completely from Redux and its nested items
      dispatch(removeCateringPackageCompletely(packageToRemove.id));
      
      // Update checkoutCart to remove only packages from current restaurant
      const updatedCheckoutCart = checkoutCart.filter(checkoutPkg => {
        // Remove the specific package being deleted
        if (checkoutPkg.id === packageToRemove.id) {
          return false;
        }
        // Keep packages from other restaurants
        if (checkoutPkg.restaurant_id && cateringRestaurantId && 
            checkoutPkg.restaurant_id !== cateringRestaurantId) {
          return true;
        }
        // Keep packages without restaurant_id (backward compatibility)
        return true;
      });
      
      dispatch(setCateringCheckoutCart(updatedCheckoutCart));
      
      // Update local ordered packages (already filtered by restaurant)
      const updatedPackages = (orderedPackages || []).filter(p => p.id !== packageToRemove.id);
      setOrderedPackages(updatedPackages);

      // Totals will be recalculated automatically via useEffect

      // Close confirmation dialog
      setPackageToRemove(null);

      // Check if there are any packages left in the ENTIRE cart (across all restaurants)
      // This is important for combined orders where packages might be from different restaurants
      const remainingPackagesInCart = cateringCartPackages.filter(pkg => pkg.id !== packageToRemove.id);
      
      // Only navigate back if this was the LAST package in the entire cart
      if (remainingPackagesInCart.length === 0) {
        console.log('✅ Last package removed, navigating to packages screen');
        navigate('/catering-packages');
      } else {
        console.log(`✅ Package removed, ${remainingPackagesInCart.length} package(s) remaining in cart`);
      }
    }
  };

  // Cancel package removal
  const cancelRemovePackage = () => {
    setPackageToRemove(null);
  };





  const handleBack = () => {
    navigate(-1);
  };

  const handleShareQuotation = () => {
    console.log("Share Quotation clicked for catering packages:", orderedPackages);
    console.log("Notes:", notes);
    setShowShareModal(true);
  };

  const handleProceedToPayment = () => {
    console.log("Proceed to Payment clicked for catering packages:", orderedPackages);
    console.log("Notes:", notes);
    // Navigate to catering checkout page
    navigate('/catering-checkout');
  };

  // Payment status label logic similar to orderSummary
  const paymentStatusLabel = useMemo(() => {
    const statuses = [
      { status: "Pending", label: "Pending Payment" },
      { status: "Failed", label: "Payment Failed" },
      { status: "Submitted", label: "Payment Successful" },
    ];
    return (
      statuses.find((s) => s.status === paymentStatus)?.label || paymentStatus
    );
  }, [paymentStatus]);

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleCloseBankModal = () => {
    setShowBankModal(false);
  };

  const handleAddToCart = async () => {
    try {
      // Filter packages by current restaurant before adding to checkout cart
      const restaurantFilteredPackages = (orderedPackages || []).filter(pkg => {
        // If package has restaurant_id, filter by it
        if (pkg.restaurant_id && cateringRestaurantId) {
          return pkg.restaurant_id === cateringRestaurantId;
        }
        // If no restaurant_id in package, include it (backward compatibility)
        return true;
      });
      
      // Update Redux state to push filtered packages to checkoutCart
      dispatch(setCateringCheckoutCart(restaurantFilteredPackages));
      
      // Save current notes as checkout notes (snapshot when added to cart)
      setCheckoutNotes(notes);
      
      // Show proceed to payment button and share quotation button
      setShowProceedToPayment(true);
      setShowShareQuotationAfterAdd(true);
      
      console.log('Packages added to checkout cart:', restaurantFilteredPackages);
    } catch (error) {
      console.error('Error updating cart state:', error);
    }
  };

  const handleUpdateCart = async () => {
    try {
      // Check if in unpaid mode - we need to call the update API
      if (actualUnpaidMode && reduxUnpaidOrderId) {
        setIsModifyingOrder(true);
        setModifyOrderError(null);

        // Check if this is a combined order by looking at the packages
        // Combined orders have multiple packages with originalOrderId set
        const isCombinedOrder = orderedPackages.length > 1 || (orderedPackages.length > 0 && orderedPackages.some(pkg => pkg.originalOrderId));
        let updatePayload;
        let response;

        if (isCombinedOrder) {
          // Combined unpaid order update
          console.log('🎯 Updating COMBINED unpaid order:', {
            orderId: reduxUnpaidOrderId,
            combineOrderId: reduxUnpaidCombineOrderId,
            packagesCount: orderedPackages.length
          });

          // Use the actual combine_catering_order_id from Redux
          // This is the ID from the database combine_catering_order table, NOT the individual order ID
          const combineOrderId = reduxUnpaidCombineOrderId || reduxUnpaidOrderId;

          console.log('✅ Using combine_catering_order_id:', combineOrderId);

          updatePayload = transformCombinedUnpaidOrderPayload({
            packages: orderedPackages,
            filters,
            deliveryCharge,
            discountPrice,
            combineOrderId
          });

          console.log('Combined unpaid order update payload:', updatePayload);

          // Call the combined unpaid order update API with the combine_catering_order_id
          response = await updateCombinedCateringUnpaidOrder(combineOrderId, updatePayload);
        } else {
          // Single unpaid order update
          console.log('📦 Updating SINGLE unpaid order:', reduxUnpaidOrderId);

          updatePayload = transformModifyOrderPayload({
            packages: orderedPackages,
            filters,
            deliveryCharge,
            discountPrice
          });

          console.log('Single unpaid order update payload:', updatePayload);

          // Call the regular unpaid order update API
          response = await updateCateringUnpaidOrder(reduxUnpaidOrderId, updatePayload);
        }

        if (response && response.data) {
          console.log('Unpaid order updated successfully:', response.data);
          
          // Navigate to checkout after successful update
          setIsModifyingOrder(false);
          navigate('/catering-checkout');
        } else {
          throw new Error('Failed to update unpaid order. Please try again.');
        }
        
        return; // Exit early for unpaid mode
      }

      // Regular cart update (non-unpaid mode)
      // Filter packages by current restaurant before updating checkout cart
      const restaurantFilteredPackages = (orderedPackages || []).filter(pkg => {
        // If package has restaurant_id, filter by it
        if (pkg.restaurant_id && cateringRestaurantId) {
          return pkg.restaurant_id === cateringRestaurantId;
        }
        // If no restaurant_id in package, include it (backward compatibility)
        return true;
      });
      
      // Update the existing packages in checkoutCart with current modifications
      // Only update packages from the current restaurant
      const updatedCheckoutCart = checkoutCart.map(checkoutPkg => {
        // Only update if the checkout package is from the current restaurant
        const isFromCurrentRestaurant = !checkoutPkg.restaurant_id || 
          (checkoutPkg.restaurant_id === cateringRestaurantId);
        
        if (isFromCurrentRestaurant) {
          const currentPkg = restaurantFilteredPackages.find(pkg => pkg.id === checkoutPkg.id);
          return currentPkg ? currentPkg : checkoutPkg;
        }
        
        // Keep packages from other restaurants unchanged
        return checkoutPkg;
      });

      // Add any new packages that might not be in checkout cart yet
      restaurantFilteredPackages.forEach(currentPkg => {
        const existsInCheckout = updatedCheckoutCart.some(cp => cp.id === currentPkg.id);
        if (!existsInCheckout) {
          updatedCheckoutCart.push(currentPkg);
        }
      });

      dispatch(setCateringCheckoutCart(updatedCheckoutCart));
      
      // Save current notes as checkout notes (snapshot when updated in cart)
      setCheckoutNotes(notes);
      
      // Reset modification flag
      setHasPackageBeenModified(false);
      
      console.log('Cart updated with modified packages:', updatedCheckoutCart);
    } catch (error) {
      console.error('Error updating cart:', error);
      setModifyOrderError(error.message || 'Failed to update order. Please try again.');
      setIsModifyingOrder(false);
    }
  };

  const handleModifyOrder = async () => {
    try {
      setIsModifyingOrder(true);
      setModifyOrderError(null);

      // Get the order ID from Redux
      if (!reduxModifyOrderId) {
        throw new Error('Order ID not found. Cannot modify order.');
      }

      // Check if this is a combined order by looking at the packages
      // Combined orders have multiple packages with originalOrderId set
      const isCombinedOrder = orderedPackages.length > 1 || (orderedPackages.length > 0 && orderedPackages.some(pkg => pkg.originalOrderId));
      let modifyPayload;
      let response;

      if (isCombinedOrder) {
        // Combined order modification
        console.log('🎯 Modifying COMBINED order:', {
          orderId: reduxModifyOrderId,
          packagesCount: orderedPackages.length
        });

        // Use reduxModifyOrderId as combineOrderId
        const combineOrderId = reduxModifyOrderId;

        modifyPayload = transformCombinedModifyOrderPayload({
          packages: orderedPackages,
          filters,
          deliveryCharge,
          discountPrice,
          combineOrderId
        });

        console.log('Combined order modify payload:', modifyPayload);

        // Call the combined modify order API
        response = await updateCombinedCateringPaidOrder(reduxModifyOrderId, modifyPayload);
      } else {
        // Single order modification
        console.log('📦 Modifying SINGLE order:', reduxModifyOrderId);

        modifyPayload = transformModifyOrderPayload({
          packages: orderedPackages,
          filters,
          deliveryCharge,
          discountPrice
        });

        console.log('Single order modify payload:', modifyPayload);

        // Call the regular modify order API
        response = await updateCateringPaidOrder(reduxModifyOrderId, modifyPayload);
      }

      if (response && response.data) {
        console.log('Order modified successfully:', response.data);
        
        // Clean up modify order mode flags from Redux
        dispatch(clearModifyOrderMode());
        
        // Show success message or navigate back
        alert('Order modified successfully!');
        navigate(-1);
      } else {
        throw new Error('Failed to modify order. Please try again.');
      }
    } catch (error) {
      console.error('Error modifying order:', error);
      setModifyOrderError(error.message || 'Failed to modify order. Please try again.');
    } finally {
      setIsModifyingOrder(false);
    }
  };


  // Handle add-on selection from modal
  const handleAddOnSelect = (addOnType) => {
    if (itemForAddOnSelection) {
      const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === itemForAddOnSelection.id));
      if (pkg) {
        const itemWithAddOnType = {
          ...itemForAddOnSelection,
          quantity: 1,
          add_on_type: addOnType,
          price: `KWD ${getCateringItemPrice(itemForAddOnSelection, filters.pax, addOnType).toFixed(3)}`
        };
        dispatch(addItemToCateringPackage({ packageId: pkg.id, item: itemWithAddOnType }));
      }
      setItemForAddOnSelection(null);
    }
  };

  // Toggle complimentary count for any item (add-on or regular)
  const toggleComplimentary = (item, newComplimentaryCount) => {
    const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === item.id && (it.add_on_type || null) === (item.add_on_type || null)));
    if (pkg) {
      dispatch(addItemToCateringPackage({ 
        packageId: pkg.id, 
        item: { ...item, complimentaryCount: newComplimentaryCount } 
      }));
    }
  };

  // Handle modal close handlers
  const handleAddOnModalClose = () => {
    setShowAddOnModal(false);
    setItemForAddOnSelection(null);
  };

  const handleRemoveWarningModalClose = () => {
    setShowRemoveWarningModal(false);
    setItemForRemoveWarning(null);
    setAddOnItemsForWarning([]);
    setPackageInfoForRemoveWarning(null);
  };

  return (
    <div className="w-full h-full bg-white flex flex-col items-center relative">
      {/* Add-On Selection Modal */}
      {showAddOnModal && itemForAddOnSelection && (
        <AddOnSelectionModal
          item={itemForAddOnSelection}
          onSelect={handleAddOnSelect}
          onClose={handleAddOnModalClose}
        />
      )}

      {/* Remove Item Warning Modal */}
      {showRemoveWarningModal && itemForRemoveWarning && (
        <RemoveItemWarningModal
          item={itemForRemoveWarning}
          addOnItems={addOnItemsForWarning}
          packageInfo={packageInfoForRemoveWarning}
          onClose={handleRemoveWarningModalClose}
        />
      )}

      {disabled && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-600 mb-2">Please select a time slot</h3>
            <p className="text-gray-500">Choose a time slot to view your order summary</p>
          </div>
        </div>
      )}
      <div className={`w-full max-w-[500px] overflow-y-auto scrollbar-hide p-4 pb-40 flex flex-col ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="border border-gray-200 w-full p-4 rounded-md mt-5 flex flex-col flex-grow">
          <div className="flex justify-between items-center mb-1">
            <h2 className="text-lg font-bold text-[#959FA8]">
              {cateringRestaurantName}
            </h2>
            <p className="text-sm text-[#959FA8]">{formattedDate}</p>
          </div>
          {orderedPackages && orderedPackages.length > 0 ? orderedPackages.map((pkg, index) => (
            <React.Fragment key={pkg.id}>
              {/* Add separator between packages (but not before first one) */}
              {index > 0 && (
                <div className="my-6">
                  <img
                    src="/Images/Menu/hr.png"
                    className="w-full"
                    alt="separator"
                  />
                </div>
              )}
              
              <div className="flex flex-col">
                {/* Restaurant Name for this package (if different or it's additional service) */}
                {(index > 0 || pkg.isAdditionalService) && (
                  <div className="flex justify-between items-center mb-1">
                    <h2 className="text-lg font-bold text-[#959FA8]">
                      {brandList?.find(brand => brand.id === pkg.restaurant_id)?.name_en || cateringRestaurantName}
                    </h2>
                  </div>
                )}
                
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-bold text-[#959FA8]">
                    {pkg?.name_en || pkg?.title}
                  </h2>
                  <button
                    onClick={() => handleRemovePackage(pkg.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200"
                    title="Remove package from cart"
                    aria-label={`Remove ${pkg?.name_en || pkg?.title} package from cart`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                <div className="flex justify-between items-center mb-3">
                  {(() => {
                    // Check if this is a standalone package (Additional Setup)
                    const packageName = pkg.name_en || pkg.title || '';
                    const isStandaloneSetup = packageName.toLowerCase().includes('additional setup');
                    const hasStandalone = isStandaloneSetup && (
                      pkg.items?.some(item => item.price_type === "Standalone Item Based") ||
                      pkg.categories?.some(cat => cat.catering_service_item?.some(item => item.price_type === "Standalone Item Based"))
                    );
                    
                    // Check if package has a valid PAX range
                    const minPaxValue = pkg.min_pax;
                    const maxPaxValue = pkg.max_pax;
                    const hasValidPaxRange = minPaxValue && maxPaxValue && 
                      minPaxValue !== "" && maxPaxValue !== "" &&
                      parseInt(minPaxValue) > 0 && parseInt(maxPaxValue) > 0;
                    
                    // For standalone packages, show "Standalone Items"
                    // For packages without valid PAX range, don't show PAX at all
                    let paxDisplay = null;
                    let paxToUse = filters.pax; // Default for price calculation (main package PAX)
                    
                    if (hasStandalone) {
                      paxDisplay = "Standalone Items";
                      // For standalone packages, still need to use package PAX for price calculation
                      // even though we don't display it
                      if (pkg.isAdditionalService) {
                        const minPax = pkg.min_pax ? parseInt(pkg.min_pax) : 20;
                        paxToUse = pkg.pax || minPax;
                      }
                    } else if (pkg.isAdditionalService && !hasValidPaxRange) {
                      // Don't show PAX for additional services without valid PAX range
                      paxDisplay = null;
                      // But still use package PAX for price calculation
                      const minPax = pkg.min_pax ? parseInt(pkg.min_pax) : 20;
                      paxToUse = pkg.pax || minPax;
                    } else {
                      // Show PAX for packages with valid PAX range
                      if (pkg.isAdditionalService) {
                        // Get PAX from the package itself (already set by PaxSelector)
                        const minPax = pkg.min_pax ? parseInt(pkg.min_pax) : 20;
                        paxToUse = pkg.pax || minPax;
                      } else {
                        paxToUse = filters.pax;
                      }
                      paxDisplay = `${paxToUse} PAX`;
                    }
                    
                    return (
                      <>
                        {paxDisplay && (
                          <p className="text-lg font-bold text-[#959FA8]">
                            {paxDisplay}
                          </p>
                        )}
                        <p className={`text-lg font-bold text-[#959FA8] ${!paxDisplay ? 'ml-auto' : ''}`}>
                          KWD {(() => {
                            // Use the calculated paxToUse for price
                            return calculatePackagePrice(pkg, paxToUse).toFixed(3);
                          })()}
                        </p>
                      </>
                    );
                  })()}
                </div>

                {/* Display items for this specific package */}
                {pkg.items && pkg.items.length > 0 && (
                  <div className="ml-4 mb-4">
                    {pkg.items.map((item) => (
                      <div key={item.id} className="mb-2">
                        {item.has_variants ? (
                          // Render variant items
                          item.items?.map((variantItem, variantIndex) => (
                            <div
                              key={variantItem.variantId}
                              className="text-sm text-[#959FA8] font-semibold"
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                  <div className="flex items-center mr-2">
                                    <button
                                      onClick={() =>
                                        handleRemoveVariantCartItem(item, variantItem)
                                      }
                                      className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                    >
                                      <span
                                        className="inline-block"
                                        style={{ marginBottom: "2px" }}
                                      >
                                        -
                                      </span>
                                    </button>
                                    <span className="mx-1 text-xs">
                                      {variantItem.quantity}
                                    </span>
                                    <button
                                      onClick={() =>
                                        handleAddVariantCartItem(item, variantItem)
                                      }
                                      className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                    >
                                      <span
                                        className="inline-block"
                                        style={{
                                          marginLeft: "0.5px",
                                          marginBottom: "1px",
                                        }}
                                      >
                                        +
                                      </span>
                                    </button>
                                  </div>
                                  <span>{item.name || item.name_en}</span>
                                </div>
                                <span>
                                  KWD{" "}
                                  {(() => {
                                    const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
                                    const totalDisplayPrice = calculateItemDisplayPriceInPackage(item, variantItem, pkg, paxToUse);
                                    return totalDisplayPrice.toFixed(3);
                                  })()}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 ml-[62px]">
                                {getSelectedOptions(variantItem.selectedVariant)}
                              </div>
                              {variantItem.instructions && (
                                <div
                                  className="text-xs text-gray-400 ml-[62px] italic truncate"
                                  title={variantItem.instructions}
                                >
                                  Note: {variantItem.instructions}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          // Render non-variant items
                          <div className="flex flex-col gap-1">
                            <div className="text-sm text-[#959FA8] font-semibold flex justify-between items-center">
                              <div className="flex items-center">
                                <div className="flex items-center mr-2">
                                  <button
                                    onClick={() => handleRemoveCartItem(item)}
                                    className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                  >
                                    <span
                                      className="inline-block"
                                      style={{ marginBottom: "2px" }}
                                    >
                                      -
                                    </span>
                                  </button>
                                  <span className="mx-1 text-xs">
                                    {item.quantity}
                                  </span>
                                  <button
                                    onClick={() => handleAddCartItem(item)}
                                    className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                  >
                                    <span
                                      className="inline-block"
                                      style={{
                                        marginLeft: "0.5px",
                                        marginBottom: "1px",
                                      }}
                                    >
                                      +
                                    </span>
                                  </button>
                                </div>
                                <span>
                                  {item.name_en || item.name}
                                  {item.add_on_type && (
                                    <span className="ml-1 text-xs text-gray-500 capitalize">
                                      ({item.add_on_type})
                                    </span>
                                  )}
                                  {item.complimentaryCount > 0 && (
                                    <span className="ml-1 text-xs text-green-600 font-semibold">
                                      [{item.complimentaryCount} comp.]
                                    </span>
                                  )}
                                </span>
                              </div>
                              <span>
                                KWD{" "}
                                {(() => {
                                  const paxToUse = pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax;
                                  const totalDisplayPrice = calculateItemDisplayPriceInPackage(item, null, pkg, paxToUse);
                                  return totalDisplayPrice.toFixed(3);
                                })()}
                              </span>
                            </div>
                            {/* Complimentary counter for add-on items */}
                            {item.add_on_type && item.quantity > 0 && (
                              <div className="flex items-center gap-2 ml-[62px]">
                                <span className="text-[10px] text-gray-600">Complimentary:</span>
                                <div className="flex items-center bg-white rounded-md h-[18px] px-1 border border-gray-300">
                                  <button
                                    onClick={() => {
                                      const newCount = Math.max(0, (item.complimentaryCount || 0) - 1);
                                      toggleComplimentary(item, newCount);
                                    }}
                                    disabled={(item.complimentaryCount || 0) === 0}
                                    className="w-4 h-4 rounded flex items-center justify-center text-xs text-gray-600 font-bold disabled:opacity-30"
                                  >
                                    -
                                  </button>
                                  <span className="h-4 text-xs w-4 text-center font-semibold text-gray-700">
                                    {item.complimentaryCount || 0}
                                  </span>
                                  <button
                                    onClick={() => {
                                      const newCount = Math.min(item.quantity, (item.complimentaryCount || 0) + 1);
                                      toggleComplimentary(item, newCount);
                                    }}
                                    disabled={(item.complimentaryCount || 0) >= item.quantity}
                                    className="w-4 h-4 rounded flex items-center justify-center text-xs text-gray-600 font-bold disabled:opacity-30"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Setup Note Section for each package */}
                <div className="mt-4">
                  <img
                    src="/Images/Menu/hr.png"
                    className="w-full mb-3"
                    alt="separator"
                  />
                  <h2 className="text-lg font-bold text-[#959FA8] mb-3">
                    Setup Note
                  </h2>
                  <div className="space-y-2">
                    {/* Service Duration */}
                    {pkg.service_duration && (
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-sm text-[#959FA8]">
                          <span className="font-semibold">Service Duration:</span> {pkg.service_duration}
                        </div>
                      </div>
                    )}
                    
                    {/* Setup Location */}
                    {pkg.setup_location && (
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div className="text-sm text-[#959FA8]">
                          <span className="font-semibold">Setup Location:</span> {pkg.setup_location}
                        </div>
                      </div>
                    )}
                    
                    {/* Setup Time */}
                    {pkg.setup_time && (
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div className="text-sm text-[#959FA8]">
                          <span className="font-semibold">Setup Time:</span> {pkg.setup_time}
                        </div>
                      </div>
                    )}
                    
                    {/* Electric Outlet */}
                    {pkg.electric_outlet && (
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <div className="text-sm text-[#959FA8]">
                          <span className="font-semibold">Electric Outlet:</span> Required
                        </div>
                      </div>
                    )}
                    
                    {/* Water Supply */}
                    {pkg.water_supply && (
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <div className="text-sm text-[#959FA8]">
                          <span className="font-semibold">Water Supply:</span> {pkg.water_supply_specs || 'Required'}
                        </div>
                      </div>
                    )}
                    
                    {/* Special Notes */}
                    {pkg.special_notes_en && (
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-sm text-[#959FA8]">
                          <span className="font-semibold">Special Notes:</span> {pkg.special_notes_en}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          )) : (
            <div className="text-center text-gray-500 py-8">
              No packages selected
            </div>
          )}

          {/* Old Setup Note Section - Remove this */}
          {false && orderedPackages && orderedPackages.length > 0 && orderedPackages[0] && (
            <>
              <div className="mt-4">
                <img
                  src="/Images/Menu/hr.png"
                  className="w-full mb-3"
                  alt="separator"
                />
                <h2 className="text-lg font-bold text-[#959FA8] mb-3">
                  Setup Note
                </h2>
                <div className="space-y-2">
                  {/* Service Duration */}
                  {orderedPackages[0].service_duration && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Service Duration:</span> {orderedPackages[0].service_duration}
                      </div>
                    </div>
                  )}
                  
                  {/* Setup Time */}
                  {orderedPackages[0].setup_time && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Setup Time:</span> {orderedPackages[0].setup_time}
                      </div>
                    </div>
                  )}
                  
                  {/* Electric Outlet */}
                  {orderedPackages[0].electric_outlet && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Electric Outlet:</span> Required
                      </div>
                    </div>
                  )}
                  
                  {/* Water Supply */}
                  {orderedPackages[0].water_supply && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Water Supply:</span> {orderedPackages[0].water_supply_specs || 'Required'}
                      </div>
                    </div>
                  )}
                  
                  {/* Special Notes */}
                  {orderedPackages[0].special_notes_en && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Special Notes:</span> {orderedPackages[0].special_notes_en}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="mt-4">
            <img
              src="/Images/Menu/hr.png"
              className="w-full mb-4"
              alt="separator"
            />
            
            {/* Total Payment Section */}
            <div className="border border-gray-300 rounded-lg p-4 mb-4">
              <div className="mb-3">
                <h2 className="text-lg font-bold text-[#959FA8]">Total Payment</h2>
              </div>
              
              <div className="space-y-3">
                {/* Per-Package Breakdown */}
                {orderedPackages && orderedPackages.length > 0 && orderedPackages.map((pkg, index) => {
                  // Check if package has a valid PAX range
                  const minPaxValue = pkg.min_pax;
                  const maxPaxValue = pkg.max_pax;
                  const hasValidPaxRange = minPaxValue && maxPaxValue && 
                    minPaxValue !== "" && maxPaxValue !== "" &&
                    parseInt(minPaxValue) > 0 && parseInt(maxPaxValue) > 0;
                  
                  // Determine PAX to use - ALWAYS use package PAX for additional services
                  let paxToUse;
                  if (pkg.isAdditionalService) {
                    // Use package's own PAX (set by PaxSelector or defaulted to min_pax)
                    const minPax = pkg.min_pax ? parseInt(pkg.min_pax) : 20;
                    paxToUse = pkg.pax || minPax;
                  } else {
                    // Main package uses filter PAX
                    paxToUse = filters.pax;
                  }
                  
                  const packageBasePrice = calculateTotalPackageBasePrice([pkg], paxToUse);
                  const packageChargeableItems = calculateTotalChargeableItemsCost([pkg], paxToUse);
                  const packageSubtotal = packageBasePrice + packageChargeableItems;
                  
                  return (
                    <div key={pkg.id} className="space-y-1">
                      {/* Package Header */}
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-[#959FA8]">
                          {pkg.name_en || pkg.title} {pkg.isAdditionalService && '(Additional Service)'}
                        </span>
                      </div>
                      
                      {/* Package Service Items */}
                      <div className="flex justify-between pl-3">
                        <span className="text-xs text-[#959FA8]">
                          Service Items
                          {/* Only show PAX if package has valid PAX range */}
                          {(pkg.isAdditionalService && hasValidPaxRange) || !pkg.isAdditionalService ? ` (${paxToUse} PAX)` : ''}
                        </span>
                        <span className="text-xs text-[#959FA8]">KWD {packageBasePrice.toFixed(3)}</span>
                      </div>
                      
                      {/* Package Add Ons */}
                      {packageChargeableItems > 0 && (
                        <div className="flex justify-between pl-3">
                          <span className="text-xs text-[#959FA8]">Add Ons</span>
                          <span className="text-xs text-[#959FA8]">KWD {packageChargeableItems.toFixed(3)}</span>
                        </div>
                      )}
                      
                      {/* Package Subtotal */}
                      <div className="flex justify-between pl-3 pt-1 border-t border-gray-200">
                        <span className="text-sm font-semibold text-[#959FA8]">Subtotal</span>
                        <span className="text-sm font-semibold text-[#959FA8]">KWD {packageSubtotal.toFixed(3)}</span>
                      </div>
                      
                      {/* Separator between packages */}
                      {index < orderedPackages.length - 1 && (
                        <div className="border-t border-gray-300 my-2"></div>
                      )}
                    </div>
                  );
                })}
                
                {/* Overall Summary Section */}
                {orderedPackages && orderedPackages.length > 0 && (
                  <>
                    <div className="border-t-2 border-gray-400 my-3"></div>
                    
                    {/* Total Service Items */}
                    <div className="flex justify-between">
                      <span className="text-sm text-[#959FA8]">Total Service Items</span>
                      <span className="text-sm text-[#959FA8]">KWD {totalPackageBasePrice.toFixed(3)}</span>
                    </div>
                    
                    {/* Total Add Ons */}
                    <div className="flex justify-between">
                      <span className="text-sm text-[#959FA8]">Total Add Ons</span>
                      <span className="text-sm text-[#959FA8]">KWD {totalChargeableItemsCost.toFixed(3)}</span>
                    </div>
                    
                    {/* Delivery Charges - Editable */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#959FA8]">Delivery Charges</span>
                      {isEditingDeliveryCharge ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            max="20.000"
                            value={deliveryChargeInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty string or valid number input (including 0)
                              if (value === '' || value === '0' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                                setDeliveryChargeInput(value);
                              }
                            }}
                            onBlur={() => {
                              // Validate and save on blur
                              let newCharge = deliveryChargeInput === '' ? 0 : parseFloat(deliveryChargeInput);
                              if (isNaN(newCharge) || newCharge < 0) {
                                newCharge = 0;
                              } else if (newCharge > 20.000) {
                                newCharge = 20.000;
                              }
                              setDeliveryCharge(newCharge);
                              dispatch(setCateringDeliveryCharge(newCharge));
                              setDeliveryChargeInput(newCharge.toFixed(3));
                              setIsEditingDeliveryCharge(false);
                              console.log("💰 Delivery charge edited and saved to Redux:", newCharge);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur(); // Trigger onBlur
                              } else if (e.key === 'Escape') {
                                setDeliveryChargeInput(deliveryCharge.toFixed(3));
                                setIsEditingDeliveryCharge(false);
                              }
                            }}
                            className="w-24 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:border-amber-400"
                            autoFocus
                          />
                          <span className="text-xs text-gray-500">KWD</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#959FA8]">KWD {deliveryCharge.toFixed(3)}</span>
                          <button
                            onClick={() => {
                              setDeliveryChargeInput(deliveryCharge.toFixed(3));
                              setIsEditingDeliveryCharge(true);
                            }}
                            className="text-xs text-amber-600 hover:text-amber-800 underline"
                            title="Edit delivery charges (Max: 20.000 KWD)"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* VAT */}
                    <div className="flex justify-between">
                      <span className="text-sm text-[#959FA8]">VAT</span>
                      <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                    </div>
                    
                    <div className="border-t border-gray-300 my-2"></div>
                    
                    {/* Subtotal (before discount) */}
                    <div className="flex justify-between">
                      <span className="text-sm text-[#959FA8]">Subtotal</span>
                      <span className="text-sm text-[#959FA8]">KWD {(subtotal + deliveryCharge).toFixed(3)}</span>
                    </div>
                    
                    {/* Discount - Editable */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#959FA8]">Discount</span>
                      {isEditingDiscount ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            max={(subtotal + deliveryCharge).toFixed(3)}
                            value={discountInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                                setDiscountInput(value);
                              }
                            }}
                            onBlur={() => {
                              let newDiscount = parseFloat(discountInput);
                              const maxDiscount = subtotal + deliveryCharge;
                              if (isNaN(newDiscount) || newDiscount < 0) {
                                newDiscount = 0;
                              } else if (newDiscount > maxDiscount) {
                                newDiscount = maxDiscount;
                              }
                              setDiscountPrice(newDiscount);
                              dispatch(setCateringDiscountPrice(newDiscount));
                              setDiscountInput(newDiscount.toFixed(3));
                              setIsEditingDiscount(false);
                              console.log("💰 Discount edited and saved to Redux:", newDiscount);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              } else if (e.key === 'Escape') {
                                setDiscountInput(discountPrice.toFixed(3));
                                setIsEditingDiscount(false);
                              }
                            }}
                            className="w-24 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:border-amber-400"
                            autoFocus
                          />
                          <span className="text-xs text-gray-500">KWD</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#959FA8]">KWD {discountPrice.toFixed(3)}</span>
                          <button
                            onClick={() => {
                              setDiscountInput(discountPrice.toFixed(3));
                              setIsEditingDiscount(true);
                            }}
                            className="text-xs text-amber-600 hover:text-amber-800 underline"
                            title={`Edit discount (Max: ${(subtotal + deliveryCharge).toFixed(3)} KWD)`}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="border-t-2 border-gray-400 my-2"></div>
                    
                    {/* Total Amount */}
                    <div className="flex justify-between">
                      <span className="text-base font-bold text-[#959FA8]">Total Amount</span>
                      <span className="text-base font-bold text-[#959FA8]">KWD {totalPayable.toFixed(3)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Show original total in modify order mode */}
            {actualModifyMode && reduxModifyOrderOriginalTotal && (
              <>
                <div className="mb-2 flex justify-between">
                  <span className="text-sm text-gray-500 font-semibold">
                    Required Total (Paid)
                  </span>
                  <span className="text-xs text-gray-500 font-semibold">
                    KWD {reduxModifyOrderOriginalTotal.toFixed(3)}
                  </span>
                </div>
                {isTotalMismatch && (
                  <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded">
                    <div className="flex items-center text-amber-700">
                      <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-semibold">
                        {totalPayable > reduxModifyOrderOriginalTotal 
                          ? `Remove KWD ${(totalPayable - reduxModifyOrderOriginalTotal).toFixed(3)} to match required total`
                          : `Add KWD ${(reduxModifyOrderOriginalTotal - totalPayable).toFixed(3)} to match required total`
                        }
                      </span>
                    </div>
                  </div>
                )}
                {!isTotalMismatch && (
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center text-green-700">
                      <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-semibold">
                        Total matches required amount ✓
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>


          <div className="mt-2 w-full">
            <textarea
              className="border border-gray-300 w-full h-[100px] p-3 rounded-md text-sm font-semibold text-[#959FA8]"
              placeholder="Notes;"
              value={notes}
              onChange={(e) => dispatch(setCateringCartNotes(e.target.value))}
            ></textarea>
          </div>

          {/* Modify Order Error Display */}
          {modifyOrderError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-red-800">
                    Error Modifying Order
                  </h4>
                  <p className="text-xs text-red-700 mt-1">
                    {modifyOrderError}
                  </p>
                  <button 
                    onClick={() => setModifyOrderError(null)}
                    className="text-xs text-red-600 hover:text-red-800 mt-1 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}



          {/* Payment status display - similar to orderSummary */}
          {showPendingPayment && (
            <div className="flex flex-col justify-center items-center mt-8 mb-4">
              <button
                className={`w-full sm:w-[222px] h-10 text-lg font-semibold p-2 rounded ${
                  paymentStatusLabel === "Pending Payment"
                    ? "bg-[#F7B501] text-white" // Styles for Pending Payment
                    : paymentStatusLabel === "Payment Failed"
                    ? "bg-[#F70101] text-white" // Styles for Payment Failed
                    : paymentStatusLabel === "Payment Successful"
                    ? "bg-[#00AF0D] text-white" // Styles for Payment Successful
                    : ""
                }`}
              >
                {paymentStatusLabel}
              </button>

              {/* Additional text below Pending Payment */}
              {paymentStatusLabel === "Pending Payment" && (
                <div className="text-center mt-2 px-4 max-w-xs">
                  <p className="text-sm text-gray-700">
                    Payment link has been sent to the customer. Waiting payment
                    to be completed.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="w-full bg-white pt-4">
            <div className="flex flex-col items-center">
              {showPaymentOptions ? (
                // Catering Checkout Page Buttons
                <div className="flex flex-row gap-3 w-full max-w-[412px]">
                  <button
                    onClick={handleBack}
                    className="w-[120px] h-10 text-[#86604D] border border-[#86604D] p-2 text-sm font-semibold rounded hover:bg-[#86604D] hover:text-white transition-colors"
                  >
                    Back
                  </button>
                  {showPendingPayment ? (
                    <button
                      onClick={() => console.log('Resend Payment Link')}
                      className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors"
                    >
                      <div className="flex items-center justify-around">
                        <span className="text-sm px-2">Resend Payment Link</span>
                        <img
                          src="/Images/Menu/resend.svg"
                          alt="resend"
                          className="ml-2"
                        />
                      </div>
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        console.log('Proceed to Payment');
                        setShowPendingPayment(true);
                        setPaymentStatus("Pending");
                      }}
                      className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors"
                    >
                      Proceed to Payment
                    </button>
                  )}
                </div>
              ) : showPackageDetailButtons ? (
                // Package Detail Page Buttons
                <div className="flex flex-col gap-3 w-full max-w-[412px]">
                  {/* Share Quotation Button - show after adding to cart */}
                  {/* {showShareQuotationAfterAdd && (
                    <button
                      onClick={handleShareQuotation}
                      className="w-full h-10 text-lg font-semibold bg-[#13305D] text-white p-2 rounded flex items-center justify-center"
                    >
                      Share Quotation <ShareQuotationIcon className="ml-2 w-4 h-4" />
                    </button>
                  )} */}
                  
                  <div className="flex flex-row gap-3">
                    <button
                      onClick={handleBack}
                      className="w-[120px] h-10 text-[#86604D] border border-[#86604D] p-2 text-sm font-semibold rounded hover:bg-[#86604D] hover:text-white transition-colors"
                    >
                      Back
                    </button>
                    {actualUnpaidMode ? (
                      // Unpaid mode: Show "Proceed to Checkout" or "Modify & Proceed to Checkout" based on modification state
                      // Note: Don't disable based on validation - allow proceeding even if incomplete (validation happens on checkout page)
                      <button
                        onClick={hasPackageBeenModified ? handleUpdateCart : handleProceedToPayment}
                        className="flex-1 h-10 text-sm font-semibold p-2 rounded transition-colors bg-[#86604D] text-white hover:bg-[#6B4A3A]"
                        title={hasPackageBeenModified ? 'Update order and proceed to checkout' : 'Proceed to checkout and send payment link'}
                      >
                        {hasPackageBeenModified ? 'Modify & Proceed to Checkout' : 'Proceed to Checkout'}
                      </button>
                    ) : showProceedToPayment && !hasPackageBeenModified ? (
                      <button
                        onClick={handleProceedToPayment}
                        className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors"
                      >
                        Proceed to Checkout
                      </button>
                    ) : hasPackageBeenModified ? (
                      <button
                        onClick={handleUpdateCart}
                        disabled={!packageValidation.isValid}
                        className={`flex-1 h-10 text-sm font-semibold p-2 rounded transition-colors ${packageValidation.isValid
                          ? 'bg-[#86604D] text-white hover:bg-[#6B4A3A]'
                          : 'bg-[#86604D] opacity-50 text-white cursor-not-allowed'
                          }`}
                        title={!packageValidation.isValid ? packageValidation.message : 'Update package in cart'}
                      >
                        Update Cart
                      </button>
                    ) : actualModifyMode ? (
                      <button
                        onClick={handleModifyOrder}
                        disabled={!packageValidation.isValid || isModifyingOrder || isTotalMismatch}
                        className={`flex-1 h-10 text-sm font-semibold p-2 rounded transition-colors ${
                          packageValidation.isValid && !isModifyingOrder && !isTotalMismatch
                            ? 'bg-[#86604D] text-white hover:bg-[#6B4A3A]'
                            : 'bg-[#86604D] opacity-50 text-white cursor-not-allowed'
                        }`}
                        title={
                          !packageValidation.isValid 
                            ? packageValidation.message 
                            : isTotalMismatch
                            ? `Total must be exactly KWD ${reduxModifyOrderOriginalTotal?.toFixed(3)} (no additions or reductions allowed)`
                            : 'Complete order modification'
                        }
                      >
                        {isModifyingOrder ? 'Modifying...' : 'Modify Order'}
                      </button>
                    ) : !isPackageInCheckoutCart ? (
                      <button
                        onClick={handleAddToCart}
                        disabled={!canAddToCart}
                        className={`flex-1 h-10 text-sm font-semibold p-2 rounded transition-colors ${canAddToCart
                          ? 'bg-[#86604D] text-white hover:bg-[#6B4A3A]'
                          : 'bg-[#86604D] opacity-50 text-white cursor-not-allowed'
                          }`}
                        title={!packageValidation.isValid ? packageValidation.message : 'Add package to cart'}
                      >
                        Add to Cart
                      </button>
                    ) : (
                      <div className="flex-1 h-10 flex items-center justify-center text-sm text-gray-500 bg-gray-100 rounded">
                        Package in Cart
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Catering Packages Page Buttons
                <>
                  <button
                    onClick={handleShareQuotation}
                    className="w-full max-w-[412px] h-10 text-lg font-semibold bg-[#13305D] text-white p-2 rounded flex items-center justify-center mb-4"
                  >
                    Share Quotation <ShareQuotationIcon className="ml-2 w-4 h-4" />
                  </button>
                  <div className="flex flex-row gap-3 w-full max-w-[412px]">
                    <button
                      onClick={handleBack}
                      className="w-[120px] h-10 text-[#86604D] border border-[#86604D] p-2 text-sm font-semibold rounded"
                    >
                      Back
                    </button>
                    {showProceedToPayment ? (
                      <button
                        onClick={handleProceedToPayment}
                        className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors"
                      >
                        Proceed to Payment
                      </button>
                    ) : (
                      <button
                        onClick={handleProceedToPayment}
                        className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded"
                      >
                        Proceed to Payment
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* {showShareModal && <ShareQuotationModal onClose={handleCloseShareModal} />} */}
      {showBankModal && <ShareBankDetailsModal onClose={handleCloseBankModal} />}

      {/* Package Removal Confirmation Modal */}
      {packageToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Remove Package</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove <span className="font-semibold">"{(packageToRemove.name_en && packageToRemove.name_en.trim()) || (packageToRemove.title && packageToRemove.title.trim()) || (packageToRemove.name && packageToRemove.name.trim()) || 'this package'}"</span> from your catering cart?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelRemovePackage}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemovePackage}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CateringOrderSummary; 